/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import * as Sentry from "@sentry/react";
import "@advapacs/common/src/App.scss";
import React, { useEffect, useMemo, useState } from "react";
import { AuthenticatedRoutes, UnauthenticatedRoutes } from "./routes";
import { useAppDispatch } from "./config/store";
import { ResponsiveAction } from "./reducers/slices/responsiveSlice";
import {
  getConfiguration,
  getCsrfToken,
  getUser,
} from "./pages/unauthenticated/ducks/operations";
import { IRootState } from "./config/reducers";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  AppAlert,
  AppLoader,
  ConfirmModal,
  MIN_WIDTH_DESKTOP,
  MIN_WIDTH_TABLET,
  NavigationInterceptor,
  prodModeLog,
  setSentryTags,
  NetworkStatusAlert,
} from "@advapacs/common";
import "@advapacs/common/src/services/i18n";
import { useFlag, useUnleashContext } from "@unleash/proxy-client-react";
import { rememberMeFunction } from "./pages/authenticated/account/ducks/operations";
import { useLocation } from "react-router-dom";
import i18next from "i18next";
import { ConnectSocialModal } from "./components/Modal/ConnectSocialModal";
import axios from "axios";
import { UnauthenticatedActions } from "./pages/unauthenticated/ducks/slices";
import {
  setFaroEnabled,
  setFaroInfoLogEnabled,
} from "./services/initializeFaro";
// modal flow
// EULA Modal => accepted => show first connect => Show remember me.
function AppFC() {
  const dispatch = useAppDispatch();
  const updateUnleashContext = useUnleashContext();
  const { pathname } = useLocation();
  const { apiLoading } = useSelector((state: IRootState) => state.errorSlice);
  const [showModalRememberMe, setShowModalRememberMe] = useState(false);
  const [isShowConnectFirstTime, setIsShowConnectFirstTime] = useState(false);
  const { isAuthenticated, sessionHasBeenFetched, account } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );
  const { eulaDetail, loaded: eulaLoaded } = useSelector(
    (state: IRootState) => state.userLicenseReducer
  );
  /**
   * Show remember me modal only first time after logged
   * the local storage remember item will be undefined.
   * After click close button it will set false and after that every refresh page will don't show it.
   */

  const changeLanguage = async () => {
    prodModeLog("Page language: en");
    await i18next.changeLanguage("en");
  };

  const resize = () => {
    dispatch(ResponsiveAction.setMobile(window.innerWidth < MIN_WIDTH_TABLET));
    dispatch(
      ResponsiveAction.setMobileOnly(window.innerWidth < MIN_WIDTH_TABLET)
    );
    dispatch(
      ResponsiveAction.setTablet(
        window.innerWidth >= MIN_WIDTH_TABLET &&
          window.innerWidth < MIN_WIDTH_DESKTOP
      )
    );
  };

  /**
   * Concurrent logged multiple tabs.
   * @param event.
   */
  const storageFunction = (event: StorageEvent) => {
    if (event.key === "logged") {
      dispatch(getUser());
    }
  };

  const handleConfirmRemember = () => {
    rememberMeFunction()
      .then(() => {
        localStorage.setItem("remember", account.id); // remember with key is patient id, next time after login success browser will check this to show the remember me function.
      })
      .catch(() => {
        localStorage.setItem("remember", "");
      })
      .finally(() => {
        setShowModalRememberMe(false);
      });
  };

  const eulaAccepted = useMemo(() => {
    return eulaLoaded && (!eulaDetail?.eula || eulaDetail?.eula?.accepted);
  }, [eulaLoaded, eulaDetail]);

  const allowPageToShowModalFirstConnect = useMemo(() => {
    return (
      pathname !== "/MyAccount" && // not account page , cause it already have connect button
      pathname !== "/terms-of-service"
    ); // dont show in user license page. (Will change to link terms-of-service for next ticket.)
  }, [pathname]);

  const showRememberMeModal = useMemo(() => {
    return (
      isAuthenticated &&
      sessionHasBeenFetched &&
      showModalRememberMe &&
      !eulaDetail?.eula?.accepted && // show the EULA modal first, and Remember Modal after that if condition is accepted.
      !pathname.includes("/OAuthCallback") && // dont show it on OAUTH callback page.
      !isShowConnectFirstTime // the value belong social login, if user don't have any connect accounts with social method, it will show the modal first time connect.
    );
  }, [
    eulaDetail?.eula,
    isAuthenticated,
    sessionHasBeenFetched,
    pathname,
    isShowConnectFirstTime,
    showModalRememberMe,
  ]);

  const handleSkipSocialConnect = async () => {
    try {
      await axios.post("/authenticatedPatient/skipExternalIdentityConnection");
      setIsShowConnectFirstTime(false);
      dispatch(
        UnauthenticatedActions.setOfferExternalIdentityConnectionIsFalse()
      );
    } catch (error) {
    } finally {
      setIsShowConnectFirstTime(false);
    }
  };

  useEffect(() => {
    window.addEventListener("storage", storageFunction);
    return () => {
      window.addEventListener("storage", storageFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Update context.
   */
  // send region params in unauthenticated context.
  useEffect(() => {
    const regionDetected = localStorage.getItem(
      `${window.location.host}.region`
    );
    if (regionDetected) {
      updateUnleashContext({
        // @ts-ignore
        region: localStorage.getItem(`${window.location.host}.region`),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && sessionHasBeenFetched) {
      const rememberMeValue = localStorage.getItem("remember");
      // check logic show remember me modal
      setShowModalRememberMe(
        !rememberMeValue || localStorage.getItem("remember") !== account.id
      );
      updateUnleashContext({
        // @ts-ignore
        customerId: account?.customerId,
        userId: account?.id,
        sessionId: account?.sessionId,
        region: localStorage.getItem(`${window.location.host}.region`),
      });
      setSentryTags({
        customerId: account?.customerId,
        userId: account?.id,
        sessionId: account?.sessionId,
      });
      const accessLink = localStorage.getItem("accessId");
      if (accessLink) {
        dispatch(getConfiguration(accessLink, false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, account, sessionHasBeenFetched]);

  useEffect(() => {
    const shouldShowModalConnectAsFirstTime =
      account?.offerExternalIdentityConnection && // no connect before.
      localStorage.getItem("skip_in_session") !== "true" && // local storage value for life of cookie.
      allowPageToShowModalFirstConnect &&
      eulaAccepted; // should accept user license first or dont have content and accepted.
    setIsShowConnectFirstTime(shouldShowModalConnectAsFirstTime as boolean);
  }, [account, allowPageToShowModalFirstConnect, eulaAccepted]);
  /**
   * Init authentication flow and events listener.
   */
  useEffect(() => {
    dispatch(getCsrfToken());
    changeLanguage(); // set default language
    window.addEventListener("resize", resize);
    window.addEventListener("storage", storageFunction);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("storage", storageFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (apiLoading) {
    return <AppLoader.Loader />;
  }

  return (
    <>
      <NetworkStatusAlert />
      {showRememberMeModal && (
        <ConfirmModal
          header="Remember Me"
          confirmBtnName="Yes"
          confirmBtnVariant="primary"
          cancelBtnName="No"
          cancelBtnVariant="secondary"
          children={
            <>
              <div className="m-b-xxs text-dark text-center m-t-xs">
                Do you want to remember this browser for 7 days?
              </div>
              <AppAlert.SimpleAlert
                variant="warning"
                className="m-t-xxs m-b-none mt-2"
                message="Anyone with access to the link will be able to view your
                data on this browser."
                dismissible={false}
              />
            </>
          }
          cancel={() => {
            localStorage.setItem("remember", "");
            setShowModalRememberMe(false);
          }}
          confirm={handleConfirmRemember}
          show={showModalRememberMe}
        />
      )}
      {!isAuthenticated && sessionHasBeenFetched && <UnauthenticatedRoutes />}
      {isAuthenticated && sessionHasBeenFetched && (
        <NavigationInterceptor>
          {isShowConnectFirstTime ? (
            <ConnectSocialModal
              onConfirm={handleSkipSocialConnect}
              show={isShowConnectFirstTime}
              onClose={() => {
                setIsShowConnectFirstTime(false);
                localStorage.setItem("skip_in_session", "true");
              }}
            />
          ) : (
            <></>
          )}
          <AuthenticatedRoutes />
        </NavigationInterceptor>
      )}
    </>
  );
}

const App = (): React.JSX.Element => {
  const { t } = useTranslation();

  const enabledFaroInfoLog = useFlag("faro-info-log");
  useEffect(() => {
    setFaroInfoLogEnabled(enabledFaroInfoLog);
    setFaroEnabled(true);
  }, [enabledFaroInfoLog]);

  return (
    <Sentry.ErrorBoundary
      fallback={() => <AppLoader.AppError t={t} />}
      dialogOptions={{
        title: t("error_messages.error"),
      }}
      showDialog
    >
      <AppFC />
    </Sentry.ErrorBoundary>
  );
};

export default App;
